// store/slices/customerSlice.ts
import {
  CreateCustomerPayload,
  Customer,
  customersApi,
  UpdateCustomerPayload,
} from "@/services/customersApi";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface CustomerState {
  customers: Customer[];
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  selectedCustomer: Customer | null;
  loading: boolean;
  error: string | null;
  updating: boolean;
}

const initialState: CustomerState = {
  customers: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  },
  selectedCustomer: null,
  loading: false,
  error: null,
  updating: false,
};

// Thunks
export const fetchCustomers = createAsyncThunk(
  "customers/fetchAll",
  async ({
    page,
    limit,
    search,
    areaId,
  }: {
    page: number;
    limit: number;
    search: string;
    areaId?: number;
  }) => {
    const response = await customersApi.getAll(page, limit, search, areaId);
    return response;
  },
);

export const fetchCustomerById = createAsyncThunk(
  "customers/fetchById",
  async (id: number) => {
    const { customer } = await customersApi.getById(id);
    return customer;
  },
);

export const createCustomer = createAsyncThunk(
  "customers/create",
  async (data: CreateCustomerPayload) => {
    const { customer } = await customersApi.create(data);
    return customer;
  },
);

export const updateCustomer = createAsyncThunk(
  "customers/update",
  async ({ id, data }: { id: number; data: UpdateCustomerPayload }) => {
    const { customer } = await customersApi.update(id, data);
    return customer;
  },
);

export const deleteCustomer = createAsyncThunk(
  "customers/delete",
  async (id: number) => {
    await customersApi.delete(id);
    return id;
  },
);

const customerSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSelected: (state) => {
      state.selectedCustomer = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all
    builder.addCase(fetchCustomers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCustomers.fulfilled, (state, action) => {
      state.loading = false;
      const { page } = action.meta.arg; // page number from thunk call
      if (page === 1) {
        state.customers = action.payload.customers;
      } else {
        // Only add customers that are not already in the list
        const newCustomers = action.payload.customers.filter(
          (newCust) =>
            !state.customers.some((existing) => existing.id === newCust.id),
        );
        state.customers = [...state.customers, ...newCustomers];
      }
      state.pagination = action.payload.pagination;
    });
    builder.addCase(fetchCustomers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch customers";
    });
    // Fetch by id
    builder.addCase(fetchCustomerById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchCustomerById.fulfilled, (state, action) => {
      state.loading = false;
      state.selectedCustomer = action.payload;
    });
    builder.addCase(fetchCustomerById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Failed to fetch customer";
    });
    // Create
    builder.addCase(createCustomer.pending, (state) => {
      state.updating = true;
      state.error = null;
    });
    builder.addCase(createCustomer.fulfilled, (state, action) => {
      state.updating = false;
      state.customers.unshift(action.payload);
    });
    builder.addCase(createCustomer.rejected, (state, action) => {
      state.updating = false;
      state.error = action.error.message || "Failed to create customer";
    });
    // Update
    builder.addCase(updateCustomer.pending, (state) => {
      state.updating = true;
      state.error = null;
    });
    builder.addCase(updateCustomer.fulfilled, (state, action) => {
      state.updating = false;
      const index = state.customers.findIndex(
        (c) => c.id === action.payload.id,
      );
      if (index !== -1) state.customers[index] = action.payload;
      if (state.selectedCustomer?.id === action.payload.id)
        state.selectedCustomer = action.payload;
    });
    builder.addCase(updateCustomer.rejected, (state, action) => {
      state.updating = false;
      state.error = action.error.message || "Failed to update customer";
    });
    // Delete
    builder.addCase(deleteCustomer.pending, (state) => {
      state.updating = true;
    });
    builder.addCase(deleteCustomer.fulfilled, (state, action) => {
      state.updating = false;
      state.customers = state.customers.filter((c) => c.id !== action.payload);
    });
    builder.addCase(deleteCustomer.rejected, (state, action) => {
      state.updating = false;
      state.error = action.error.message || "Failed to delete customer";
    });
  },
});

export const { clearError, resetSelected } = customerSlice.actions;
export default customerSlice.reducer;
